import React from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  MenuBook,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  PlayCircleOutline,
  CheckCircle,
  RadioButtonUnchecked,
  AssignmentOutlined
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

export default function LeftSidebar({
  hierarchicalContent = [],
  openMain,
  handleMainClick,
  handleSelectContent,
  selectedContent,
  leftOpen,
  onToggleLeft,
  progressMap = {}
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ✅ Scrollbar styling
  const scrollbarStyles = {
    scrollbarWidth: "thin",
    scrollbarColor: isDark
      ? "rgba(255, 255, 255, 0.2) transparent"
      : "rgba(0, 0, 0, 0.2) transparent",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.35)",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  };

  // ✅ Recursive render function
  const renderItems = (items, level = 0) => (
    <List disablePadding>
      {items.map((item) => {
        if (!item || !item.content_id) return null;

        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const isOpen = !!openMain[item.content_id];
        const isSelected = selectedContent?.content_id === item.content_id;

        // Check completion status
        const isCompleted = !!progressMap[item.content_id];

        // For parent module, count completion
        let moduleProgressText = "";
        let isModuleCompleted = false;
        if (hasChildren) {
          const totalLessons = item.children.filter(c => !c.isQuiz).length;
          const completedLessons = item.children.filter(c => !c.isQuiz && progressMap[c.content_id]).length;
          isModuleCompleted = totalLessons > 0 && completedLessons === totalLessons;
          if (totalLessons > 0) {
            moduleProgressText = ` (${completedLessons}/${totalLessons})`;
          }
        }

        // Render appropriate icon based on status and item type
        const renderItemIcon = () => {
          if (hasChildren) {
            return isModuleCompleted ? (
              <CheckCircle sx={{ mr: 1.25, fontSize: 18, color: "#2e7d32" }} />
            ) : (
              <PlayCircleOutline sx={{ mr: 1.25, fontSize: 18, color: theme.palette.text.secondary }} />
            );
          }

          if (item.isQuiz) {
            return isCompleted ? (
              <CheckCircle sx={{ mr: 1.25, fontSize: 18, color: "#2e7d32" }} />
            ) : (
              <AssignmentOutlined sx={{ mr: 1.25, fontSize: 18, color: "rgba(128,128,128,0.6)" }} />
            );
          }

          // Regular lesson
          if (isCompleted) {
            return <CheckCircle sx={{ mr: 1.25, fontSize: 18, color: "#2e7d32" }} />;
          }

          if (isSelected) {
            return <PlayCircleOutline sx={{ mr: 1.25, fontSize: 18, color: isDark ? "#ffa4a2" : "#e50914" }} />;
          }

          return <RadioButtonUnchecked sx={{ mr: 1.25, fontSize: 18, color: "rgba(128,128,128,0.4)" }} />;
        };

        return (
          <React.Fragment key={item.content_id}>
            <ListItemButton
              onClick={() => {
                // ✅ Always show this module’s content first
                handleSelectContent(item);

                // ✅ If it has children, also toggle expansion
                if (hasChildren) handleMainClick(item.content_id);
              }}
              selected={isSelected}
              sx={{
                pl: 2 + level * 2,
                mb: 0.3,
                borderRadius: 1.5,
                "&.Mui-selected": {
                  bgcolor: isDark ? "rgba(229,9,20,0.15)" : "rgba(229,9,20,0.06)",
                  color: isDark ? "#ffcdd2" : "#e50914",
                  "&:hover": { bgcolor: isDark ? "rgba(229,9,20,0.2)" : "rgba(229,9,20,0.1)" },
                  "& svg": {
                    color: "inherit !important"
                  }
                },
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.05)",
                },
              }}
            >
              {renderItemIcon()}

              <ListItemText
                primaryTypographyProps={{
                  fontWeight: isSelected || hasChildren ? 700 : 500,
                  fontSize: hasChildren ? 14 : 13,
                  color: isSelected 
                    ? "inherit" 
                    : (hasChildren ? theme.palette.text.primary : theme.palette.text.secondary),
                }}
                primary={`${item.title.replace(/^(Module\s+\d+)\s+\1/i, "$1").replace(/^(\d+(\.\d+)?)\s+\1/i, "$1")}${moduleProgressText}`}
              />

              {/* ✅ Expand/collapse icon works independently */}
              {hasChildren && (
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent selecting item when toggling
                    handleMainClick(item.content_id);
                  }}
                >
                  {isOpen ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              )}
            </ListItemButton>

            {hasChildren && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                {renderItems(item.children, level + 1)}
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );


  // ✅ Sidebar structure
  const sidebarContent = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        color: theme.palette.text.primary,
        transition: "background-color 0.3s ease, color 0.3s ease",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "transparent",
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 1.2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <MenuBook color="primary" />
        {leftOpen && (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ flexGrow: 1 }}
          >
            Course Content
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={onToggleLeft}
          sx={{
            color: theme.palette.text.secondary,
            "&:hover": { color: theme.palette.primary.main },
          }}
        >
          {leftOpen ? (
            <KeyboardDoubleArrowLeft sx={{ fontSize: 22 }} />
          ) : (
            <KeyboardDoubleArrowRight sx={{ fontSize: 22, ml: -1 }} />
          )}
        </IconButton>
      </Box>

      {/* Hierarchical List */}
      <Box sx={{ flex: 1, p: 1, overflowY: "auto", ...scrollbarStyles }}>
        {hierarchicalContent && hierarchicalContent.length > 0 ? (
          renderItems(hierarchicalContent)
        ) : (
          <Box sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
            <Typography variant="body2">
              📂 No course content available
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return sidebarContent;
}
